import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiError } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import Logo from "../components/Logo";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const LoginPage = () => {
  const { t, lang, setLang } = useLang();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login"); // or "register"
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user =
        mode === "login"
          ? await login(form.email, form.password)
          : await register(form.name, form.email, form.password);
      toast.success(`Bem-vindo(a), ${user.name}`);
      // Admins always land on /admin. Clients respect "from" or go to /dashboard.
      const dest = user.role === "admin"
        ? "/admin"
        : (location.state?.from || "/dashboard");
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.login.invalid);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F1EB] grid md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1765862835282-cd3d9190d246?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="StayZen home interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A1128]/30" />
        <div className="absolute bottom-12 left-12 right-12 text-[#F4F1EB]">
          <Logo size={72} variant="horizontal" className="mb-4" />
          <p className="text-sm text-[#F4F1EB]/70 max-w-sm font-light">{t.footer.tagline}</p>
        </div>
      </div>
      <div className="flex flex-col justify-between p-6 md:p-12">
        <div className="flex justify-between items-center">
          <Link to="/" className="font-serif-display text-xl text-[#0A1128]" data-testid="login-back-home">
            ← {t.nav.home}
          </Link>
          <div className="flex items-center gap-1 text-xs tracking-[0.18em]">
            <button
              onClick={() => setLang("pt")}
              className={lang === "pt" ? "text-[#C5A059]" : "text-[#0A1128]/50"}
              data-testid="login-lang-pt"
            >PT</button>
            <span className="text-[#0A1128]/30">|</span>
            <button
              onClick={() => setLang("en")}
              className={lang === "en" ? "text-[#C5A059]" : "text-[#0A1128]/50"}
              data-testid="login-lang-en"
            >EN</button>
          </div>
        </div>

        <div className="max-w-md mx-auto w-full">
          <div className="overline mb-6">{mode === "login" ? t.login.title : t.login.register_title}</div>
          <h1 className="font-serif-display text-4xl md:text-5xl font-light leading-tight text-[#0A1128]">
            {mode === "login" ? t.login.title : t.login.register_title}
          </h1>
          <p className="mt-4 text-[#3A435E] font-light">{t.login.subtitle}</p>

          <form onSubmit={onSubmit} className="mt-10 space-y-6" data-testid="login-form">
            {mode === "register" && (
              <div>
                <label className="overline block mb-2">{t.login.name}</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border-b border-[#0A1128]/20 focus:border-[#C5A059] outline-none py-3 text-[#0A1128]"
                  data-testid="login-field-name"
                />
              </div>
            )}
            <div>
              <label className="overline block mb-2">{t.login.email}</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-transparent border-b border-[#0A1128]/20 focus:border-[#C5A059] outline-none py-3 text-[#0A1128]"
                data-testid="login-field-email"
              />
            </div>
            <div>
              <label className="overline block mb-2">{t.login.password}</label>
              <div className="relative">
                <input
                  required
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full bg-transparent border-b border-[#0A1128]/20 focus:border-[#C5A059] outline-none py-3 pr-10 text-[#0A1128]"
                  data-testid="login-field-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-1.5 text-[#3A435E] hover:text-[#C5A059] transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={18} strokeWidth={1.4} /> : <Eye size={18} strokeWidth={1.4} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0A1128] text-white hover:bg-[#162243] transition-colors duration-300 px-8 py-4 text-xs tracking-[0.22em] uppercase disabled:opacity-60"
              data-testid="login-submit"
            >
              {loading ? "…" : mode === "login" ? t.login.submit : t.login.register_submit}
            </button>
          </form>

          <div className="mt-8 flex items-center gap-4 text-xs tracking-[0.22em] uppercase text-[#3A435E]/60">
            <span className="h-px bg-[#0A1128]/15 flex-1" />
            <span>{t.login.or}</span>
            <span className="h-px bg-[#0A1128]/15 flex-1" />
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            className="mt-6 w-full border border-[#0A1128]/20 bg-white text-[#0A1128] hover:bg-[#0A1128] hover:text-white transition-colors duration-300 px-8 py-4 text-xs tracking-[0.22em] uppercase flex items-center justify-center gap-3"
            data-testid="login-google"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.1z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4 5.4l.1-.1 6.2 5.2C36.9 40.2 44 34 44 24c0-1.2-.1-2.4-.4-3.5z"/>
            </svg>
            {t.login.google}
          </button>

          <div className="mt-8 text-sm text-[#3A435E] font-light">
            {mode === "login" ? (
              <>
                {t.login.register_prompt}{" "}
                <button
                  className="text-[#C5A059] link-underline"
                  onClick={() => setMode("register")}
                  data-testid="switch-to-register"
                >
                  {t.login.register_link}
                </button>
              </>
            ) : (
              <button
                className="text-[#C5A059] link-underline"
                onClick={() => setMode("login")}
                data-testid="switch-to-login"
              >
                ← {t.login.back_login}
              </button>
            )}
          </div>
        </div>

        <div className="text-xs text-[#0A1128]/40 text-center">© StayZen</div>
      </div>
    </div>
  );
};

export default LoginPage;
